import type { LeagueEntry, LeagueEntryPlayer } from '@api-service/mini-leagues';
import {
  applySubstitutions,
  resolveEffectiveMultipliers,
  type AutoSubPick,
  type PickAvailability
} from '@lib/live/team/auto-subs';

type LeagueEntryBenchPlayer = LeagueEntry['bench'][number];
type SquadPlayer = LeagueEntryPlayer | LeagueEntryBenchPlayer;

export type EntrySquadRow = {
  player: SquadPlayer;
  /** XI rows are multiplied; bench rows show what the player actually scored. */
  displayPoints: number;
  /** Whether this pick wears the armband once a blanked captain has handed it on. */
  isCaptain: boolean;
  /** Suppressed when the vice-captain has inherited the armband. */
  isViceCaptain: boolean;
};

export type ResolvedEntrySquad = {
  starting: EntrySquadRow[];
  bench: EntrySquadRow[];
  /** Members of the XI that is counting whose gameweek is over. */
  playedCount: number;
};

type EntryPick = AutoSubPick & { player: SquadPlayer };

const XI_SIZE = 11;
/** A vice-captain only scores above this once the live feed has moved the armband. */
const UNPROMOTED_VICE_CAPTAIN_MULTIPLIER = 1;

const isCounting = (player: SquadPlayer) => player.multiplier > 0;

/**
 * The live feed applies auto-subs before the entry reaches us, and records them
 * in the multipliers alone: a substituted-out starter is reported at zero or
 * below, and the substitute who replaced them at one. The finished and in-play
 * flags are only computed for players still counting, so they cannot tell a
 * blanked starter from one whose match has not started. The multipliers are the
 * same signal the entry's gameweek total was built from, which keeps the lineup
 * and the total in agreement.
 *
 * Nothing here is ever `pending`, because the feed has already finished: a
 * reserve it did not bring on is one it decided against, so he reads as `blank`
 * and the pairing carries on past him. `pending` means the opposite to the
 * rules engine — a reserve whose fixture has yet to settle, who holds the
 * vacancy rather than yielding it (FFH-3129) — and that is My Team Live's
 * problem to have, resolving live off the match channels, not this one's.
 */
const toAvailability = (pick: EntryPick): PickAvailability =>
  isCounting(pick.player) ? 'played' : 'blank';

const toPick = (
  player: SquadPlayer,
  slot: number,
  isStarter: boolean
): EntryPick => ({
  slot,
  isStarter,
  position: player.position,
  multiplier: Math.max(player.multiplier, 0),
  isCaptain: player.isCaptain,
  isViceCaptain: player.isViceCaptain,
  player
});

/**
 * Shows a mini-league entry's squad as it is actually scoring, mirroring My
 * Team Live: a blanked starter drops to the bench, the substitute who replaced
 * them takes their place in the XI, and the vice-captain wears the armband when
 * the captain did not play. The entry's `points.gameweek` already includes the
 * auto-subs; this brings the displayed lineup, and the played count, in line.
 */
export const resolveEntrySquad = (entry: LeagueEntry): ResolvedEntrySquad => {
  // The entry has no slot numbers, so synthesise them: XI in array order, bench
  // in substitution-priority order.
  const startingPicks = entry.starting.map((player, index) =>
    toPick(player, index + 1, true)
  );
  const benchPicks = [...entry.bench]
    .sort((a, b) => a.priority - b.priority)
    .map((player, index) => toPick(player, XI_SIZE + index + 1, false));

  const picks = [...startingPicks, ...benchPicks];
  const availability = new Map(
    picks.map((pick) => [pick.slot, toAvailability(pick)])
  );
  // Only the pairing is taken from the rules engine, so a substitute lands in
  // the row of the starter they replaced. Who counts, and at what multiplier,
  // is already settled by the feed.
  const { substitutions } = resolveEffectiveMultipliers(
    picks,
    availability,
    entry.activeChip
  );

  // The feed hands the armband on by raising the vice-captain's multiplier to
  // the captain's, so a vice-captain scoring above one is wearing it.
  const captain = picks.find((pick) => pick.isCaptain);
  const viceCaptain = picks.find((pick) => pick.isViceCaptain);
  const hasArmbandMoved =
    viceCaptain !== undefined &&
    viceCaptain.multiplier > UNPROMOTED_VICE_CAPTAIN_MULTIPLIER;
  const captainSlot = hasArmbandMoved ? viceCaptain.slot : captain?.slot;

  const toRow = (pick: EntryPick, isInXi: boolean): EntrySquadRow => {
    const isCaptain = captainSlot === pick.slot;
    return {
      player: pick.player,
      displayPoints: isInXi
        ? pick.player.points * pick.multiplier
        : pick.player.points,
      isCaptain,
      isViceCaptain: pick.isViceCaptain && !isCaptain
    };
  };

  const placed = applySubstitutions(startingPicks, benchPicks, substitutions);
  const starting = placed.starting.map((pick) => toRow(pick, true));

  return {
    starting,
    bench: placed.bench.map((pick) => toRow(pick, false)),
    playedCount: starting.filter((row) => row.player.isFinished).length
  };
};
